import React, { useState, useEffect, ChangeEvent, useCallback } from 'react';
import { AccountTree } from '@mui/icons-material';
import {
    Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Select, MenuItem,
    FormControl, Card, CardContent, CardHeader, Dialog, DialogTitle, DialogContent,
    DialogActions, ButtonGroup, Pagination, CircularProgress, Box, Typography, Paper, SelectChangeEvent
} from '@mui/material';
import authService from '../api-authorization/AuthorizeService';
import Loader from '../Loader';
import { useToast } from '../../hooks/useToast';
import { ToastNotification } from '../common/ToastNotification';
import { formatApiError } from '../../utils/errorFormatter';
import { RefundTreeDialog, VoucherGraph } from './RefundTreeDialog';

// --- Типы и константы ---

const statusDisplayMap: { [key: string]: string } = {
    'Ok': 'Возможен',
    'TemporarilyBlocked': 'Временно заблокирован',
    'Blocked': 'Заблокирован'
};

const statusFilterOptions = {
    'Ok': 'Возможен',
    'TemporarilyBlocked': 'Временно заблокирован',
    'Blocked': 'Заблокирован'
};

interface Refund {
    id: number;
    terminalId: number;
    terminalName?: string;
    sum: number;
    dateTime: string;
    cardNo?: string;
    lastManualOp?: string;
    text?: string;
    info?: string;
    state: string;
    refundCode?: string;
    externalId?: string;
    closeOnTerminalId?: number;
    properties?: { [key: string]: string };
}

interface RefundOp {
    id: number;
    dataTime: string;
    comment: string;
    terminalName: string;
}

interface Filters {
    id: string;
    terminalName: string;
    sum: string;
    date: string;
    state: string;
    cardNo: string;
    text: string;
    info: string;
}

interface PagedResult<T> {
    items: T[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

// Хук для дебаунса
function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

export const Refunds: React.FC = () => {
    // --- Состояния ---
    const [refunds, setRefunds] = useState<Refund[]>([]);
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [filters, setFilters] = useState<Filters>({
        id: '',
        terminalName: '',
        sum: '',
        date: '',
        state: '',
        cardNo: '',
        text: '',
        info: ''
    });

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(50);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [refreshCounter, setRefreshCounter] = useState(0);

    const debouncedFilters = useDebounce(filters, 500);

    const [detailsModal, setDetailsModal] = useState(false);
    const [historyModal, setHistoryModal] = useState(false);
    const [treeModal, setTreeModal] = useState(false);
    const [confirmationModal, setConfirmationModal] = useState(false);
    
    const [selectedRefund, setSelectedRefund] = useState<Refund | null>(null);
    const [refundHistory, setRefundHistory] = useState<RefundOp[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [refundTree, setRefundTree] = useState<VoucherGraph | null>(null);
    const [loadingTree, setLoadingTree] = useState(false);
    const [actionToConfirm, setActionToConfirm] = useState<(() => void) | null>(null);

    const { toast, showError, showSuccess, handleClose } = useToast();

    // Состояния для ролей пользователя
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userRoles, setUserRoles] = useState<string[]>([]);

    // --- Переключатели модальных окон ---
    const toggleDetailsModal = () => setDetailsModal(!detailsModal);
    const toggleHistoryModal = () => setHistoryModal(!historyModal);
    const toggleTreeModal = () => setTreeModal(!treeModal);
    const toggleConfirmationModal = () => setConfirmationModal(!confirmationModal);

    // --- Функции для работы с API ---
    const fetchRefunds = useCallback(async (isInitialLoad: boolean) => {
        if (isInitialLoad) {
            setLoading(true);
        } else {
            setTableLoading(true);
        }
        const activeFilters = Object.fromEntries(
            Object.entries(debouncedFilters).filter(([, value]) => value && value.trim() !== '')
        );
        const query = new URLSearchParams({
            ...activeFilters,
            pageNumber: pageNumber.toString(),
            pageSize: pageSize.toString()
        }).toString();

        try {
            const response = await fetch(`api/refunds?${query}`);
            if (response.ok) {
                const pagedResult: PagedResult<Refund> = await response.json();
                setRefunds(pagedResult.items);
                setTotalPages(pagedResult.totalPages);
                setTotalCount(pagedResult.totalCount);
            } else {
                const errorMsg = await formatApiError(response, 'Не удалось загрузить возвраты');
                showError(errorMsg);
            }
        } catch (error: any) {
            showError('Произошла сетевая ошибка при загрузке возвратов: ' + (error.message || ''));
        } finally {
            if (isInitialLoad) {
                setLoading(false);
            } else {
                setTableLoading(false);
            }
        }
    }, [debouncedFilters, pageNumber, pageSize, showError]);

    const fetchRefundHistory = async (refundId: number) => {
        setLoadingHistory(true);
        try {
            const response = await fetch(`api/refunds/${refundId}/history`);
            if (response.ok) {
                const data = await response.json();
                setRefundHistory(data);
            } else {
                const errorMsg = await formatApiError(response, 'Не удалось загрузить историю возврата');
                showError(errorMsg);
            }
        } catch (error: any) {
            setRefundHistory([]);
            showError('Произошла сетевая ошибка при загрузке истории: ' + (error.message || ''));
        } finally {
            setLoadingHistory(false);
        }
    };
    
    const fetchRefundTree = async (refundId: number) => {
        setLoadingTree(true);
        setRefundTree(null);
        try {
            const response = await fetch(`api/refunds/${refundId}/tree`);
            if (response.ok) {
                setRefundTree(await response.json());
            } else {
                const errorMsg = await formatApiError(response, 'Не удалось загрузить граф возврата');
                showError(errorMsg);
            }
        } catch (error: any) {
            showError('Произошла сетевая ошибка при загрузке графа возврата: ' + (error.message || ''));
        } finally {
            setLoadingTree(false);
        }
    };

    const confirmAction = (action: () => void) => {
        setActionToConfirm(() => action);
        toggleConfirmationModal();
    };

    const handleConfirm = () => {
        if (actionToConfirm) {
            actionToConfirm();
        }
        toggleConfirmationModal();
    };
    
    const blockRefund = async (refundId: number) => {
        try {
            const response = await fetch(`api/refunds/${refundId}/block`, { method: 'POST' });
            if (response.ok) {
                showSuccess('Возврат успешно заблокирован');
                setRefreshCounter(c => c + 1);
                // Обновляем историю, если модальное окно истории открыто
                if (historyModal) {
                    await fetchRefundHistory(refundId);
                }
                toggleDetailsModal();
            } else {
                const errorMsg = await formatApiError(response, 'Не удалось заблокировать возврат');
                showError(errorMsg);
            }
        } catch (error: any) {
            showError('Произошла ошибка при блокировке возврата: ' + (error.message || ''));
        }
    };

    const unblockRefund = async (refundId: number) => {
        try {
            const response = await fetch(`api/refunds/${refundId}/unblock`, { method: 'POST' });
            if (response.ok) {
                showSuccess('Возврат успешно разблокирован');
                setRefreshCounter(c => c + 1);
                // Обновляем историю, если модальное окно истории открыто
                if (historyModal) {
                    await fetchRefundHistory(refundId);
                }
                toggleDetailsModal();
            } else {
                const errorMsg = await formatApiError(response, 'Не удалось разблокировать возврат');
                showError(errorMsg);
            }
        } catch (error: any) {
            showError('Произошла ошибка при разблокировке возврата: ' + (error.message || ''));
        }
    };

    // --- Эффекты ---
    useEffect(() => {
        fetchRefunds(true);
    }, [fetchRefunds]);

    useEffect(() => {
        fetchRefunds(false);
    }, [debouncedFilters, pageNumber, refreshCounter, fetchRefunds]);

    // Сброс страницы при изменении фильтров
    useEffect(() => {
        setPageNumber(1);
    }, [debouncedFilters]);

    // Эффект для получения ролей пользователя
    useEffect(() => {
        const populateAuthenticationState = async () => {
            const [authenticated, user] = await Promise.all([
                authService.isAuthenticated(),
                authService.getUser()
            ]);
            setIsAuthenticated(authenticated);
            if (user && user.profile && user.profile.role) {
                const roles = Array.isArray(user.profile.role) ? user.profile.role : [user.profile.role];
                setUserRoles(roles);
            } else {
                setUserRoles([]);
            }
        };

        const subscription = authService.subscribe(() => populateAuthenticationState());
        populateAuthenticationState();

        return () => {
            authService.unsubscribe(subscription);
        };
    }, []);

    // Функция для проверки наличия роли
    const hasRole = useCallback((rolesToCheck: string[]) => {
        if (!isAuthenticated) return false;
        return rolesToCheck.some(role => userRoles.includes(role));
    }, [isAuthenticated, userRoles]);

    const canManageRefunds = hasRole(['Admin', 'RefundManager']);
    const isAdmin = hasRole(['Admin']);

    // --- Обработчики событий ---
    const handleFilterChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };
    
    const handleSelectFilterChange = (e: SelectChangeEvent<string>) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        setPageNumber(page);
    };

    const openDetailsModal = (refund: Refund) => {
        setSelectedRefund(refund);
        toggleDetailsModal();
    };

    const openHistoryModal = (refund: Refund) => {
        setSelectedRefund(refund);
        fetchRefundHistory(refund.id);
        toggleHistoryModal();
    };

    const openTreeModal = (refund: Refund) => {
        setSelectedRefund(refund);
        setTreeModal(true);
        fetchRefundTree(refund.id);
    };

    // --- Рендеринг ---
    if (loading) {
        return <Loader />;
    }

    return (
        <Box>
            <ToastNotification toast={toast} onClose={handleClose} />

            <Card>
                <CardHeader title="Список возвратов" />
                <CardContent>
                    <Box sx={{ position: 'relative' }}>
                        {tableLoading && (
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                zIndex: 2
                            }}>
                                <CircularProgress />
                            </Box>
                        )}
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Терминал</TableCell>
                                        <TableCell>Сумма</TableCell>
                                        <TableCell>Дата</TableCell>
                                        <TableCell>Статус</TableCell>
                                        <TableCell>Номер карты</TableCell>
                                        <TableCell>Текст</TableCell>
                                        <TableCell>Инфо</TableCell>
                                        <TableCell style={{ width: '1%' }}></TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><TextField size="small" variant="standard" name="id" placeholder="ID..." value={filters.id} onChange={handleFilterChange} autoComplete="off" /></TableCell>
                                        <TableCell><TextField size="small" variant="standard" name="terminalName" placeholder="Название..." value={filters.terminalName} onChange={handleFilterChange} autoComplete="off" /></TableCell>
                                        <TableCell><TextField size="small" variant="standard" name="sum" placeholder="Сумма..." value={filters.sum} onChange={handleFilterChange} autoComplete="off" /></TableCell>
                                        <TableCell><TextField size="small" variant="standard" type="date" name="date" value={filters.date} onChange={handleFilterChange} autoComplete="off" InputLabelProps={{ shrink: true }} /></TableCell>
                                        <TableCell>
                                            <FormControl size="small" variant="standard" fullWidth>
                                                <Select name="state" value={filters.state} onChange={handleSelectFilterChange} displayEmpty>
                                                    <MenuItem value=""><em>Все</em></MenuItem>
                                                    {Object.entries(statusFilterOptions).map(([key, value]) => (<MenuItem key={key} value={key}>{value}</MenuItem>))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell><TextField size="small" variant="standard" name="cardNo" placeholder="Карта..." value={filters.cardNo} onChange={handleFilterChange} autoComplete="off" /></TableCell>
                                        <TableCell><TextField size="small" variant="standard" name="text" placeholder="Текст..." value={filters.text} onChange={handleFilterChange} autoComplete="off" /></TableCell>
                                        <TableCell><TextField size="small" variant="standard" name="info" placeholder="Инфо..." value={filters.info} onChange={handleFilterChange} autoComplete="off" /></TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {refunds.map(refund => (
                                        <TableRow key={refund.id} hover>
                                            <TableCell>{refund.id}</TableCell>
                                            <TableCell>{refund.terminalName || refund.terminalId}</TableCell>
                                            <TableCell>{refund.sum.toFixed(2)}</TableCell>
                                            <TableCell>{new Date(refund.dateTime).toLocaleString()}</TableCell>
                                            <TableCell>{statusDisplayMap[refund.state] || refund.state}</TableCell>
                                            <TableCell>{refund.cardNo}</TableCell>
                                            <TableCell>{refund.text}</TableCell>
                                            <TableCell>{refund.info}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <ButtonGroup size="small" variant="outlined">
                                                    <Button onClick={() => openDetailsModal(refund)}>Детали</Button>
                                                    <Button onClick={() => openHistoryModal(refund)}>История</Button>
                                                    <Button startIcon={<AccountTree />} onClick={() => openTreeModal(refund)}>Граф</Button>
                                                </ButtonGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {totalPages > 1 && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                            <Pagination count={totalPages} page={pageNumber} onChange={handlePageChange} color="primary" />
                        </Box>
                    )}
                    {totalCount > 0 && <Typography variant="caption" display="block" sx={{ mt: 1 }}>Всего записей: {totalCount}</Typography>}
                </CardContent>
            </Card>

            {selectedRefund && (
                <>
                    <Dialog open={detailsModal} onClose={toggleDetailsModal}>
                        <DialogTitle>Детали возврата #{selectedRefund.id}</DialogTitle>
                        <DialogContent>
                            <TableContainer component={Paper} sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableBody>
                                        <TableRow><TableCell>ID:</TableCell><TableCell>{selectedRefund.id}</TableCell></TableRow>
                                        <TableRow><TableCell>Статус:</TableCell><TableCell>{statusDisplayMap[selectedRefund.state] || selectedRefund.state}</TableCell></TableRow>
                                        <TableRow><TableCell>Сумма:</TableCell><TableCell>{selectedRefund.sum.toFixed(2)}</TableCell></TableRow>
                                        <TableRow><TableCell>Дата:</TableCell><TableCell>{new Date(selectedRefund.dateTime).toLocaleString()}</TableCell></TableRow>
                                        <TableRow><TableCell>Терминал:</TableCell><TableCell>{selectedRefund.terminalName || selectedRefund.terminalId}</TableCell></TableRow>
                                        {selectedRefund.cardNo && <TableRow><TableCell>Номер карты:</TableCell><TableCell>{selectedRefund.cardNo}</TableCell></TableRow>}
                                        {selectedRefund.refundCode && <TableRow><TableCell>Код возврата:</TableCell><TableCell>{selectedRefund.refundCode}</TableCell></TableRow>}
                                        {selectedRefund.externalId && <TableRow><TableCell>Внешний ID:</TableCell><TableCell>{selectedRefund.externalId}</TableCell></TableRow>}
                                        {selectedRefund.info && <TableRow><TableCell>Инфо:</TableCell><TableCell>{selectedRefund.info}</TableCell></TableRow>}
                                        {selectedRefund.text && <TableRow><TableCell>Текст:</TableCell><TableCell>{selectedRefund.text}</TableCell></TableRow>}
                                        {selectedRefund.lastManualOp && <TableRow><TableCell>Изменен:</TableCell><TableCell>{new Date(selectedRefund.lastManualOp).toLocaleString()}</TableCell></TableRow>}
                                        {selectedRefund.closeOnTerminalId && <TableRow><TableCell>Закрыт на терминале ID:</TableCell><TableCell>{selectedRefund.closeOnTerminalId}</TableCell></TableRow>}
                                        {isAdmin && selectedRefund.properties && Object.keys(selectedRefund.properties).length > 0 && (
                                            <TableRow>
                                                <TableCell sx={{ verticalAlign: 'top' }}>Свойства:</TableCell>
                                                <TableCell>
                                                    <Table size="small">
                                                        <TableBody>
                                                            {Object.entries(selectedRefund.properties).map(([key, value]) => (
                                                                <TableRow key={key}>
                                                                    <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.25 }}>{key}</TableCell>
                                                                    <TableCell sx={{ borderBottom: 'none', py: 0.25 }}>{value}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </DialogContent>
                        <DialogActions>
                            {canManageRefunds && (
                                <>
                                    {(selectedRefund.state === 'Ok' || selectedRefund.state === 'TemporarilyBlocked') && (
                                        <Button color="error" onClick={() => confirmAction(() => blockRefund(selectedRefund.id))}>Блокировать</Button>
                                    )}
                                    {selectedRefund.state === 'TemporarilyBlocked' && (
                                        <Button color="success" onClick={() => confirmAction(() => unblockRefund(selectedRefund.id))}>Разблокировать</Button>
                                    )}
                                </>
                            )}
                            <Button onClick={toggleDetailsModal}>Закрыть</Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog open={historyModal} onClose={toggleHistoryModal} maxWidth="md" fullWidth>
                        <DialogTitle>История операций по возврату #{selectedRefund.id}</DialogTitle>
                        <DialogContent>
                            {loadingHistory ? <CircularProgress /> : 
                                refundHistory.length > 0 ? (
                                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Дата и время</TableCell>
                                                    <TableCell>Место операции</TableCell>
                                                    <TableCell>Комментарий</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {refundHistory.map(op => (
                                                    <tr key={op.id}>
                                                        <TableCell>{new Date(op.dataTime).toLocaleString()}</TableCell>
                                                        <TableCell>{op.terminalName}</TableCell>
                                                        <TableCell>{op.comment}</TableCell>
                                                    </tr>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : <Typography sx={{ mt: 2 }}>История операций отсутствует.</Typography>}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={toggleHistoryModal}>Закрыть</Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
            
            <RefundTreeDialog
                open={treeModal}
                onClose={toggleTreeModal}
                selectedRefundId={selectedRefund?.id ?? null}
                graph={refundTree}
                loading={loadingTree}
                statusLabels={statusDisplayMap}
            />

            <Dialog open={confirmationModal} onClose={toggleConfirmationModal}>
                <DialogTitle>Подтверждение действия</DialogTitle>
                <DialogContent>
                    <Typography>Вы уверены, что хотите выполнить это действие?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirm} color="primary">Подтвердить</Button>
                    <Button onClick={toggleConfirmationModal}>Отмена</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
